import { Component } from '@theme/component';
import { QuoteUpdateEvent, ThemeEvents } from '@theme/events';
import { readMmyVehicleContext } from '@theme/mmy-state';
import { QuoteStorage } from '@theme/quote-storage';

// Always import through the importmap: relative paths resolve to un-versioned CDN
// URLs, which Shopify's CDN keeps serving stale long after a deploy.
import '@theme/quote-items';
import '@theme/quote-request-contact';
import '@theme/quote-request-dealers';

const TOTAL_STEPS = 4;
const SUBMIT_STEP = 3;

/**
 * @typedef {object} QuoteRequestConfig
 * @property {string} nextLabel
 * @property {string} [submittingLabel]
 * @property {string[]} [stepHints]
 * @property {string} backLabel
 * @property {string} submitLabel
 * @property {string[]} stepLabels
 */

/**
 * @typedef {object} QuoteRequestRefs
 * @property {HTMLElement} progressNav
 * @property {HTMLElement} progressCar
 * @property {HTMLElement} contentRegion
 * @property {HTMLElement} navFooter
 * @property {HTMLElement} [submitError]
 * @property {HTMLElement} [submitNotice]
 * @property {HTMLElement} [navHint]
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLElement} nextButtonLabel
 * @property {HTMLElement} nextButtonIcon
 * @property {HTMLElement} stepPanel1
 * @property {HTMLElement} stepPanel2
 * @property {HTMLElement} stepPanel3
 * @property {HTMLElement} stepPanel4
 * @property {HTMLElement} [itemCount]
 */

/**
 * @extends {Component<QuoteRequestRefs>}
 */
class QuoteRequestComponent extends Component {
  /** @type {QuoteRequestConfig | null} */
  #config = null;

  /** @type {number} */
  #currentStep = 1;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#readConfig();
    document.addEventListener(ThemeEvents.quoteUpdate, this.#updateNextButton);
    document.addEventListener(ThemeEvents.quoteUpdate, this.#updateItemCount);
    this.addEventListener('quote-request:change', this.#updateNextButton);
    this.#setStep(1, { focus: false });
    this.#updateItemCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quoteUpdate, this.#updateNextButton);
    document.removeEventListener(ThemeEvents.quoteUpdate, this.#updateItemCount);
    this.removeEventListener('quote-request:change', this.#updateNextButton);
    window.removeEventListener('beforeunload', this.#guardUnload);
  }

  /** @returns {QuoteRequestConfig | null} */
  #readConfig() {
    const configElement = this.querySelector('[data-quote-request-config]');

    if (!(configElement instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(configElement.textContent || '');
    } catch {
      return null;
    }
  }

  goToNextStep = async () => {
    if (this.#currentStep >= TOTAL_STEPS || this.#isSubmitting) return;

    const { nextButton } = this.refs;
    if (nextButton?.disabled) return;

    if (this.#currentStep === 2 && !this.#validateContactStep()) {
      return;
    }

    if (this.#currentStep === SUBMIT_STEP) {
      if (!this.#validateDealerStep()) return;

      this.#isSubmitting = true;
      if (nextButton) nextButton.disabled = true;
      this.#showSubmitError('');
      this.#setSubmittingState(true);

      try {
        await this.#submitQuote();
        // Accepted: clear the submitted quote so the next one starts fresh, and let
        // the header badge / items list follow.
        const quote = QuoteStorage.completeSubmission();
        document.dispatchEvent(
          new QuoteUpdateEvent(quote, this.id, { source: 'quote-request-component' }),
        );
        this.#setStep(SUBMIT_STEP + 1);
      } catch (error) {
        console.error('Quote submission failed:', error);
        this.#showSubmitError(
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong submitting your quote. Please try again.',
        );
      } finally {
        this.#isSubmitting = false;
        this.#setSubmittingState(false);
        this.#updateNextButton();
      }

      return;
    }

    this.#setStep(this.#currentStep + 1);
  };

  /**
   * Show (or clear, with an empty string) the submission error message.
   * @param {string} message
   */
  #showSubmitError(message) {
    const errorElement = this.refs.submitError;
    if (!(errorElement instanceof HTMLElement)) return;
    errorElement.textContent = message;
    errorElement.hidden = !message;
  }

  /**
   * While the request is in flight: "Submitting…" on the button, the don't-leave
   * notice under it, and a leave-page guard. A slow response may already be committed
   * in NetSuite, so abandoning the page mid-flight is the one outcome to prevent.
   * @param {boolean} active
   */
  #setSubmittingState(active) {
    const { nextButtonLabel, submitNotice } = this.refs;

    if (nextButtonLabel) {
      nextButtonLabel.textContent = active
        ? this.#config?.submittingLabel || 'Submitting…'
        : this.#config?.submitLabel || 'Submit quote';
    }

    if (submitNotice instanceof HTMLElement) submitNotice.hidden = !active;

    if (active) {
      window.addEventListener('beforeunload', this.#guardUnload);
    } else {
      window.removeEventListener('beforeunload', this.#guardUnload);
    }
  }

  /** @param {BeforeUnloadEvent} event */
  #guardUnload = (event) => {
    event.preventDefault();
    event.returnValue = '';
  };

  goToPreviousStep = () => {
    if (this.#currentStep <= 1 || this.#currentStep > SUBMIT_STEP) return;

    this.#setStep(this.#currentStep - 1);
  };

  /** @returns {boolean} */
  #validateContactStep() {
    const contact = this.querySelector('quote-request-contact');

    if (contact && 'validate' in contact && typeof contact.validate === 'function') {
      return contact.validate();
    }

    return true;
  }

  /** @returns {boolean} */
  #validateDealerStep() {
    const dealers = this.querySelector('quote-request-dealers');

    if (dealers && 'validate' in dealers && typeof dealers.validate === 'function') {
      return dealers.validate();
    }

    return true;
  }

  async #submitQuote() {
    const mmy = readMmyVehicleContext();
    const payload = QuoteStorage.buildSubmitPayload({ mmy });

    console.log('Quote submission payload:', payload);

    try {
      await QuoteStorage.submitConfigured({ mmy });
    } catch (error) {
      // Allow completion when no API endpoint is configured; data remains in localStorage.
      if (
        error instanceof Error &&
        error.message.includes('not configured')
      ) {
        return;
      }

      throw error;
    }
  }

  /**
   * @param {number} step
   * @param {{ focus?: boolean }} [options]
   */
  #setStep(step, options = {}) {
    const { focus = true } = options;
    this.#showSubmitError('');
    this.#currentStep = step;
    this.dataset.currentStep = String(step);
    this.style.setProperty('--quote-step', String(step - 1));

    for (let index = 1; index <= TOTAL_STEPS; index += 1) {
      const panel = this.refs[`stepPanel${index}`];
      if (panel) panel.hidden = index !== step;
    }

    this.querySelectorAll('[data-progress-step]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const stepNumber = Number(element.dataset.progressStep);
      const isCurrent = stepNumber === step;
      const isComplete = stepNumber < step;

      element.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      element.classList.toggle('quote-progress__step--current', isCurrent);
      element.classList.toggle('quote-progress__step--complete', isComplete);
    });

    const { prevButton, nextButton, nextButtonLabel, nextButtonIcon, navFooter } = this.refs;

    if (navFooter) {
      navFooter.hidden = step > SUBMIT_STEP;
    }

    if (prevButton) {
      prevButton.hidden = step === 1 || step > SUBMIT_STEP;
    }

    if (nextButton) {
      const isSubmitStep = step === SUBMIT_STEP;

      if (nextButtonLabel) {
        nextButtonLabel.textContent = isSubmitStep
          ? this.#config?.submitLabel || 'Submit quote'
          : this.#config?.nextLabel || 'Next step';
      }

      if (nextButtonIcon) {
        nextButtonIcon.hidden = isSubmitStep;
      }

      this.#updateNextButton();
    }

    if (focus) {
      this.#focusStepContent();
    }

    if (step === 3) {
      const map = this.querySelector('map-of-dealers-map');
      if (map) {
        this.#prefillDealerSearch(map);
        this.#applyDealerCapabilityFilter(map);
      }

      requestAnimationFrame(() => {
        if (
          map &&
          'ensureInitialized' in map &&
          typeof map.ensureInitialized === 'function'
        ) {
          map.ensureInitialized();
        }
      });
    }
  }

  /**
   * The dealer step only offers dealers able to do what was chosen on the contact
   * step: installers only for "purchase and install"; for "purchase only", any
   * dealer (installers and suppliers alike sell the parts).
   * @param {Element} map
   */
  #applyDealerCapabilityFilter(map) {
    const purchaseType = QuoteStorage.getRequestData().purchaseType;
    const filter = purchaseType === 'purchase_and_install' ? 'installer' : '';

    if ('setCapabilityFilter' in map && typeof map.setCapabilityFilter === 'function') {
      map.setCapabilityFilter(filter);
    }

    const note = this.querySelector('[ref="capabilityNote"]');
    if (note instanceof HTMLElement) {
      const text = filter === 'installer' ? note.dataset.installerText : note.dataset.supplierText;
      note.textContent = text || '';
      note.hidden = !text;
    }
  }

  /**
   * Seed the dealer search with the address entered on the contact step, so the
   * dealer list opens ordered by distance from the customer. The contact step
   * already resolved that address to coordinates, so no geocode is needed; a
   * postcode-only fallback covers quotes saved before coordinates were captured.
   * Never overwrites a search the user typed themselves — only an empty box or a
   * previous prefill is replaced.
   * @param {Element} map
   */
  #prefillDealerSearch(map) {
    const input = map.querySelector('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;

    const data = QuoteStorage.getRequestData();
    const hasCoordinates = Number.isFinite(data.addressLat) && Number.isFinite(data.addressLng);
    const label = ((hasCoordinates && data.address) || data.addressPostcode || '').trim();
    if (!label) return;

    const current = input.value.trim();
    if (current === label) return;
    if (current && current !== input.dataset.prefilled) return;

    input.dataset.prefilled = label;

    if (
      hasCoordinates &&
      'setSearchLocation' in map &&
      typeof map.setSearchLocation === 'function'
    ) {
      map.setSearchLocation({ lat: Number(data.addressLat), lng: Number(data.addressLng) }, label);
      return;
    }

    input.value = label;
    // If the map is already initialized this triggers its search pipeline;
    // before initialization the value is picked up by the map's first filter.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  #focusStepContent() {
    const { contentRegion } = this.refs;
    if (!contentRegion) return;

    const panel = this.refs[`stepPanel${this.#currentStep}`];
    const heading = panel?.querySelector('[data-step-heading]') || contentRegion.querySelector('[data-step-heading]');

    if (heading instanceof HTMLElement) {
      requestAnimationFrame(() => {
        heading.focus();
      });
    }
  }

  #updateNextButton = () => {
    const { nextButton } = this.refs;
    if (!nextButton || this.#currentStep > SUBMIT_STEP) return;

    let shouldDisable = this.#isSubmitting;

    if (this.#currentStep === 1) {
      shouldDisable = shouldDisable || QuoteStorage.getItems().length === 0;
    } else if (this.#currentStep === 2) {
      const contact = this.querySelector('quote-request-contact');
      shouldDisable =
        shouldDisable ||
        !contact ||
        !('isValid' in contact) ||
        typeof contact.isValid !== 'function' ||
        !contact.isValid();
    } else if (this.#currentStep === SUBMIT_STEP) {
      const dealers = this.querySelector('quote-request-dealers');
      shouldDisable =
        shouldDisable ||
        !dealers ||
        !('isValid' in dealers) ||
        typeof dealers.isValid !== 'function' ||
        !dealers.isValid();
    }

    nextButton.disabled = shouldDisable;

    if (shouldDisable) {
      nextButton.setAttribute('aria-disabled', 'true');
    } else {
      nextButton.removeAttribute('aria-disabled');
    }

    // A disabled button can't explain itself (it never receives the click that
    // would show the step's validation), so say what the step still needs.
    // An empty quote has nothing to step through — the empty state's own
    // "Continue shopping" is the only sensible action, so the nav goes entirely.
    const emptyQuote = this.#currentStep === 1 && QuoteStorage.getItems().length === 0;
    const { navFooter, navHint } = this.refs;
    if (navFooter) navFooter.hidden = this.#currentStep > SUBMIT_STEP || emptyQuote;

    if (navHint instanceof HTMLElement) {
      const hint =
        shouldDisable && !this.#isSubmitting && !emptyQuote
          ? this.#config?.stepHints?.[this.#currentStep - 1] || ''
          : '';
      navHint.textContent = hint;
      navHint.hidden = !hint;
    }
  };

  #updateItemCount = () => {
    const { itemCount } = this.refs;
    if (!itemCount) return;

    itemCount.textContent = String(QuoteStorage.getItems().length);
  };
}

if (!customElements.get('quote-request-component')) {
  customElements.define('quote-request-component', QuoteRequestComponent);
}
